import { cn } from "@/lib/utils";
import { useI18N } from "common/hooks";
import { AccessibilityCategoryHybrid, VenueDetails } from "common/types";
import EmptyAccessibility from "@/assets/images/empty-accessibility.svg";
import AccessibilityItemHybrid from "@/features/home/VenuePreview/AccessibilityItemHybrid";
import { useEffect, useMemo, useRef, useState } from "react";
import ScrollContainer from "react-indiana-drag-scroll";
import { formatDateShort } from "common/utils/general";
import AnalyticsService from "@/services/analytics";

type AccessibilitySectionProps = {
  venueDetails: VenueDetails;
  accessibilityCategories: AccessibilityCategoryHybrid[];
  fullSize?: boolean;
  initialSection?: string;
  onLogin: () => void;
};

function AccessibilitySectionHybrid({
  venueDetails,
  accessibilityCategories,
  fullSize,
  initialSection,
  onLogin,
}: AccessibilitySectionProps) {
  const i18n = useI18N();
  const accessibilitySectionRefs = useRef(new Map());

  const [selectedSection, setSelectedSection] = useState(
    initialSection ?? "All"
  );

  const accessibilitySections = ["All"].concat(
    accessibilityCategories?.map((category) => category.catName) || []
  );

  const selectedSections = useMemo(() => {
    if (selectedSection === "All") {
      return accessibilityCategories;
    }
    return accessibilityCategories.filter(
      (category) => category.catName === selectedSection
    );
  }, [selectedSection, accessibilityCategories]);

  useEffect(() => {
    if (initialSection) {
      setSelectedSection(initialSection);
    }
  }, [initialSection]);

  const handleAccessibilitySectionTracking = (section) => {
    console.log("section", section);
    AnalyticsService.trackGAEventHandler("navigatedToAccessibilityCategoryInformation");
    AnalyticsService.trackMx("navigatedToAccessibilityCategoryInformation", {
      VenueId: venueDetails.business.id,
      VenueBrandId: venueDetails.brandId,
      VenueBrandName: venueDetails.brandName,
      VenueAddress: venueDetails.address,
      VenueName: venueDetails.name,
      VenueType: venueDetails.businessType,
      AccessibilitySection: section,
    });
  };

  return (
    <section className="px-2 md:px-0 md:mr-2">
      {accessibilitySections.length > 1 && (
        <div className="py-2">
          <ScrollContainer>
            <div className="flex flex-row space-x-4 md:space-x-6 mb-5">
              {accessibilitySections.map((section) => (
                <button
                  key={section}
                  className={cn(
                    "py-[10px] px-[16px] rounded-[20px] whitespace-nowrap text-primary-800 dark:text-primary-100 paragraph-small-medium",
                    selectedSection === section
                      ? "bg-secondary-400 dark:text-primary-900"
                      : "bg-primary-100 dark:bg-[#37354D]"
                  )}
                  onClick={() => {
                    handleAccessibilitySectionTracking(section);
                    setSelectedSection(section);
                  }}
                  aria-label={`${section} ${selectedSection === section ? "selected" : ""} `}
                >
                  {section}
                </button>
              ))}
            </div>
          </ScrollContainer>
        </div>
      )}
     

      {accessibilitySections.length > 1 &&
        venueDetails?.latestReviewDateUpdated && (
          <div className="mt-4 mb-5">
            <span className="text-primary-800 dark:text-primary-150 mr-2 mobile-h3-small-semi-bold">
              {i18n.get("lastUpdated")}:
            </span>
            <span className="text-primary-800 dark:text-primary-150  mr-2 paragraph-medium-medium">
              {formatDateShort(venueDetails.latestReviewDateUpdated)}
            </span>
          </div>
        )}

      {/* Accessibility Category & Tags */}
      <div className="flex flex-col gap-6 ">
        {selectedSections?.map((category) => (
          <div
            key={category.catName}
            ref={(ref) => {
              if (ref) {
                accessibilitySectionRefs.current.set(category.catName, ref);
              }
            }}
          >
            <AccessibilityItemHybrid
              venueId={venueDetails.id}
              businessId={venueDetails.business.id.toString()}
              category={category}
              fullSize={fullSize}
              venueName={venueDetails.name}
              onLogin={() => {
                onLogin();
              }}
              venueDetails={venueDetails}
            />
          </div>
        ))}
      </div>
      {accessibilityCategories.length === 0 && (
        <div className="items-center justify-center">
          <img
            src={EmptyAccessibility}
            alt="No search results"
            className="max-w-[70%] mx-auto my-10"
          />
          <p className="text-center desktop-h2-small-bold">
            No accessibility information yet
          </p>
          <p className="text-center paragraph-small-regular mt-3">
            Visit the venue and use our app to tag important accessibility
            information to help the community.{" "}
          </p>
        </div>
      )}
    </section>
  );
}

export default AccessibilitySectionHybrid;
